import { Directive, signal, ViewChild, WritableSignal, inject, computed, Input } from '@angular/core';
import { endOfDay, isSameDay, lastDayOfMonth, lastDayOfYear, startOfDay, startOfMonth, startOfYear, endOfMonth, endOfYear } from 'date-fns';
import { Chart, ChartOptions, ChartType, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DateTimeValue } from '../../../../api/models/date-time-value';
import { AverageEnergyResponse } from '../../../../api/models/average-energy-response';
import { AverageEnergyStatus } from '../../../../api/models/average-energy-status';
import { EnergySummary } from '../../../../api/models/energy-summary';
import { Api } from '../../../../api/api';
import { getSunriseSunset } from '../../../../api/functions';
import { LocationStateService } from '../../../../common/services/location-state.service';
import { catchError, from, of } from 'rxjs';
import { DateTime } from 'luxon';

export type Interval = 'day' | 'month' | 'year' | 'all' | 'extendedMonth';

const MILLISECONDS_PER_MINUTE = 60_000;
const MINUTES_PER_DAY = 24 * 60;

/** Intervals whose timeline is generated in sub-day steps, so `timeBucket` applies. */
const SUB_DAY_INTERVALS: Interval[] = ['day', 'extendedMonth'];

export type EnergyType = 'production' | 'consumption' | 'battery' | 'grid';
export type BatteryType = 'charge' | 'power' | 'origin';
export type EnergyCombinationType = 'performance' | 'origin';

@Directive()
export abstract class BaseChartComponent {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  protected chartData: WritableSignal<any> = signal<any>({});
  protected chartType: WritableSignal<ChartType> = signal<ChartType>('line');
  protected chartOptions: WritableSignal<ChartOptions> = signal<ChartOptions>({});

  protected api = inject(Api);
  protected weatherLocationStateService = inject(LocationStateService);

  // Use matchMedia for more robust detection and guard for non-browser contexts
  isMobile = signal<boolean>(typeof window !== 'undefined' ? window.matchMedia('(max-width: 767.98px)').matches : false);
  @Input() interval: Interval = 'day'; // default value
  @Input() loading: boolean = false;
  @Input()
  private _unit: string = 'kW';
  public get unit(): string {
    // // Adjust values based on interval
    // if (this.interval !== 'day') {
    //   return 'kwh';
    // } else {
    //   return this._unit;
    // }
    return this._unit;

  }
  public set unit(value: string) {
    this._unit = value;
  }

  /**
   * The timezone of the currently selected location. When the location has no
   * timezone set, this is `undefined` and date formatting uses the runtime default.
   */
  protected get timezone(): string | undefined {
    return this.weatherLocationStateService.currentLocation()?.timezone;
  }

  constructor() {
    // Add listeners to keep isMobile updated
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(max-width: 767.98px)');
      // Set initial value based on media query
      this.isMobile.set(mediaQuery.matches);

      // Prefer modern addEventListener; fall back to addListener for older browsers
      const mqListener = (e: MediaQueryListEvent) => this.isMobile.set(e.matches);
      // @ts-ignore - addListener exists on older browsers
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      mediaQuery.addEventListener?.('change', mqListener) || mediaQuery.addListener?.(mqListener);

      // Fallback: also respond to resize events
      window.addEventListener('resize', () => {
        this.isMobile.set(window.innerWidth < 768);
      });
    }
  }

  protected updateChartType(interval: Interval) {
    switch (interval) {
      case 'day':
        this.chartType.set('line');
        break;
      case 'month':
      case 'extendedMonth':
        this.chartType.set('bar');
        break;
      case 'year':
        this.chartType.set('bar');
        break;
      default:
        this.chartType.set('line');
    }
  }

  /**
   * Returns a "nice" tick step for symmetric axes (-max..+max),
   * so charts using the same max also use the same tick distance.
   */
  protected getSymmetricAxisTickStep(maxAbs: number): number {
    if (!Number.isFinite(maxAbs) || maxAbs <= 0) {
      return 1;
    }

    // Increase density so symmetric energy charts show more y-axis ticks.
    const targetStepsPerSide = 11;
    const rawStep = maxAbs / targetStepsPerSide;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;

    let niceStep = 1;
    if (normalized > 5) {
      niceStep = 10;
    } else if (normalized > 2) {
      niceStep = 5;
    } else if (normalized > 1) {
      niceStep = 2;
    }

    return niceStep * magnitude;
  }

  /**
   * Annotate origin charts directly on the y-axis:
   * top tick => "Verwendung", bottom tick => "Herkunft" (default labels).
   */
  protected getOriginAxisTickLabel(
    value: string | number,
    index: number,
    ticks: any[],
    labels: { top: string; bottom: string } = { top: 'Verwendung', bottom: 'Herkunft' },
  ): string {
    const numericValue = typeof value === 'number' ? value : Number(value);
    const valueLabel = Number.isFinite(numericValue) ? String(numericValue) : String(value);

    if (index === ticks.length - 1) {
      return `${labels.top} ${valueLabel}`;
    }
    if (index === 0) {
      return `${labels.bottom} ${valueLabel}`;
    }
    return valueLabel;
  }

  protected summarizeByTimestamp(arrays: DateTimeValue[][]): DateTimeValue[] {
    const summary: { [timestamp: string]: { read: string; value: number } } = {};

    arrays.forEach((array) => {
      array.forEach((item) => {
        const { read, value } = item;

        if (read != null && value != null) {
          // Convert to UTC ISO string for consistent comparison
          const date = new Date(read);
          const utcTimestamp = date.toISOString();

          if (!summary[utcTimestamp]) {
            summary[utcTimestamp] = { read: utcTimestamp, value: 0 };
          }
          summary[utcTimestamp].value += value; // Correctly sum values
        }
      });
    });
    const values = Object.values(summary);
    return values;
  }


  private getCommonDateEndOfDay(data: DateTimeValue[]): Date | null {
    const dates = data.map((d) => startOfDay(d.read!));

    const uniqueDates = new Set(dates);
    const lastDate = Array.from(uniqueDates).pop();
    return lastDate ? endOfDay(new Date(lastDate)) : null;
  }

  private getStartTimeForInterval(interval: Interval, date: Date): Date {
    switch (interval) {
      case 'day':
        return startOfDay(date);
      case 'month':
      case 'extendedMonth':
        return startOfMonth(date);
      case 'year':
        return startOfYear(date);
      case 'all':
        return startOfYear(date);
      default:
        return startOfDay(date);
    }
  }

  private getEndTimeForInterval(interval: Interval, date: Date): Date {
    switch (interval) {
      case 'day':
        return endOfDay(date);
      case 'month':
      case 'extendedMonth':
        return endOfMonth(date);
      case 'year':
        return endOfYear(date);
      case 'all':
        return endOfYear(date);
      default:
        return endOfDay(date);
    }
  }

  protected generateMissingData(
    data: DateTimeValue[],
    interval: Interval,
    date: Date,
    fillValue: number | null = 0.0,
    timeBucket?: string | null,
  ): DateTimeValue[] {
    const missingData: DateTimeValue[] = [];
    const nowTimestamp = Date.now();
    const useNullForFuture =
      interval === 'month' || interval === 'year' || interval === 'extendedMonth';

    // Get the full interval range
    const start = this.getStartTimeForInterval(interval, date);
    const end = this.getEndTimeForInterval(interval, date);

    // Create a set of existing timestamps for quick lookup
    // Convert to UTC ISO string for consistent comparison
    const existingTimestamps = new Set(
      data.map((item) => {
        const date = new Date(item.read!);
        // Convert to UTC ISO string
        return date.toISOString();
      }),
    );

    // Step width of the generated timeline: the API `timeBucket` (e.g. "5 minutes",
    // "15 minutes") when it applies, otherwise the interval default.
    const stepMinutes = this.resolveStepMinutes(interval, timeBucket, data);

    // Create a copy of start time to avoid mutating the original
    const currentTime = new Date(
      stepMinutes != null ? this.alignGridStart(start, data, stepMinutes) : start,
    );

    while (currentTime <= end) {
      // Convert current time to UTC ISO string for consistent comparison
      const utcTimestamp = currentTime.toISOString();

      if (!existingTimestamps.has(utcTimestamp)) {
        const pointTimestamp = currentTime.getTime();
        const valueForPoint =
          useNullForFuture && pointTimestamp > nowTimestamp ? null : fillValue;
        missingData.push({
          read: utcTimestamp,
          value: valueForPoint,
        });
      }
      this.incrementTime(currentTime, interval, stepMinutes);
    }
    return missingData;
  }

  protected incrementTime(date: Date, interval: Interval, stepMinutes?: number | null): void {
    if (stepMinutes != null && stepMinutes > 0) {
      date.setTime(date.getTime() + Math.round(stepMinutes * MILLISECONDS_PER_MINUTE));
      return;
    }
    switch (interval) {
      case 'day':
        date.setMinutes(date.getMinutes() + 5);
        break;
      case 'month':
        date.setDate(date.getDate() + 1);
        break;
      case 'extendedMonth':
        date.setHours(date.getHours() + 2);
        break;
      case 'year':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'all':
        // No increment — will cause loop to stop immediately
        break;
      default:
        date.setMinutes(date.getMinutes() + 5);
        break;
    }
  }

  protected getLatestTime(data: DateTimeValue[]): Date | null {
    if (data.length === 0) {
      return null; // Return null if the data is empty
    }

    const latest = data.reduce((latestTime, current) => {
      const currentTime = new Date(current.read ?? '');

      // Check if currentTime is valid before comparing
      if (isNaN(currentTime.getTime())) {
        return latestTime; // Skip invalid dates
      }

      if (!latestTime || currentTime > latestTime) {
        return currentTime;
      }
      return latestTime;
    }, new Date(0));

    // If all dates are invalid, return null
    return isNaN(latest.getTime()) ? null : latest;
  }

  protected getLatestNeededTime(data: DateTimeValue[], option: 'day' | 'month' | 'year' | 'all', date: Date): Date {
    switch (option) {
      case 'day':
        return this.getCommonDateEndOfDay(data) || endOfDay(date);
      //return endOfDay(date);
      case 'month':
        return endOfDay(lastDayOfMonth(date));
      case 'year':
      case 'all':
        return endOfDay(lastDayOfYear(date));
      default:
        return endOfDay(new Date());
    }
  }

  protected isThereData(
    timeValues: Array<DateTimeValue>
  ): boolean {
    if (!timeValues || timeValues.length === 0) {
      return false;
    }

    return timeValues.some(item =>
      typeof item.value === 'number' && item.value !== 0
    );
  }

  /**
   * The `timeBucket` reported by the API for the currently displayed response.
   * All statuses of one response share the same bucket, so the first one that
   * states it wins. Used as fallback when a caller does not pass its own bucket.
   */
  protected get responseTimeBucket(): string | undefined {
    const values = (this as unknown as { data?: AverageEnergyResponse }).data?.values;
    if (!values) {
      return undefined;
    }
    return Object.values(values).find((status) => status?.timeBucket)?.timeBucket;
  }

  /**
   * Parses an API `timeBucket` ("5 minutes", "15 minutes", "1 hour", ...) into
   * minutes. Calendar-based buckets ("1 week", "1 month", "1 year") return null
   * because their length varies — those intervals keep calendar stepping.
   */
  protected parseTimeBucketMinutes(timeBucket?: string | null): number | null {
    if (!timeBucket) {
      return null;
    }
    const match = /^\s*(\d+(?:\.\d+)?)\s*(second|minute|hour|day)s?\s*$/i.exec(timeBucket);
    if (!match) {
      return null;
    }
    const amount = Number(match[1]);
    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    switch (match[2].toLowerCase()) {
      case 'second':
        return amount / 60;
      case 'minute':
        return amount;
      case 'hour':
        return amount * 60;
      case 'day':
        return amount * MINUTES_PER_DAY;
      default:
        return null;
    }
  }

  /**
   * Step width (in minutes) for the generated timeline, or null to keep the
   * interval's calendar stepping. Resolution order: the bucket passed by the
   * caller, the bucket of the current response, then the spacing observed in the
   * data itself. Only sub-day intervals are stepped by bucket.
   */
  protected resolveStepMinutes(
    interval: Interval,
    timeBucket: string | null | undefined,
    data: DateTimeValue[],
  ): number | null {
    if (!SUB_DAY_INTERVALS.includes(interval)) {
      return null;
    }

    const bucketMinutes =
      this.parseTimeBucketMinutes(timeBucket) ?? this.parseTimeBucketMinutes(this.responseTimeBucket);
    if (bucketMinutes != null && bucketMinutes < MINUTES_PER_DAY) {
      return bucketMinutes;
    }

    return this.inferStepMinutes(data);
  }

  /**
   * Fallback for responses without a usable `timeBucket`: the most common gap
   * between consecutive points. Gaps in the data don't skew it, since only the
   * dominant spacing wins.
   */
  private inferStepMinutes(data: DateTimeValue[]): number | null {
    const timestamps = data
      .map((item) => new Date(item.read ?? '').getTime())
      .filter((timestamp) => !Number.isNaN(timestamp))
      .sort((a, b) => a - b);

    if (timestamps.length < 3) {
      return null;
    }

    const gapCounts = new Map<number, number>();
    for (let i = 1; i < timestamps.length; i++) {
      const gap = timestamps[i] - timestamps[i - 1];
      if (gap > 0 && gap < MINUTES_PER_DAY * MILLISECONDS_PER_MINUTE) {
        gapCounts.set(gap, (gapCounts.get(gap) ?? 0) + 1);
      }
    }

    let dominantGap: number | null = null;
    let dominantCount = 0;
    gapCounts.forEach((count, gap) => {
      if (count > dominantCount) {
        dominantCount = count;
        dominantGap = gap;
      }
    });

    return dominantGap == null ? null : dominantGap / MILLISECONDS_PER_MINUTE;
  }

  /**
   * Keeps the generated grid in phase with the real data, so a bucket that does
   * not start exactly at the interval start (e.g. readings at :05, :20, :35)
   * doesn't produce a second, interleaved set of points.
   */
  private alignGridStart(start: Date, data: DateTimeValue[], stepMinutes: number): Date {
    const stepMs = Math.round(stepMinutes * MILLISECONDS_PER_MINUTE);
    if (stepMs <= 0) {
      return start;
    }

    const firstTimestamp = data
      .map((item) => new Date(item.read ?? '').getTime())
      .filter((timestamp) => !Number.isNaN(timestamp))
      .sort((a, b) => a - b)[0];

    if (firstTimestamp === undefined) {
      return start;
    }

    const offset = (((firstTimestamp - start.getTime()) % stepMs) + stepMs) % stepMs;
    return offset === 0 ? start : new Date(start.getTime() + offset);
  }

  /**
   * fillMode:
   * - 'zero' (default)
   * - 'null'
   *
   * `timeBucket` is the value reported by the API for this data (e.g. "5 minutes",
   * "15 minutes"); it defines the spacing of the completed timeline.
   */
  protected completeData(
    timeValues: Array<DateTimeValue>,
    interval: Interval,
    date: Date,
    fillMode: 'zero'  | 'null' = 'zero',
    timeBucket?: string | null,
  ) {
    const rangeStart = this.getStartTimeForInterval(interval, date).getTime();
    const rangeEnd = this.getEndTimeForInterval(interval, date).getTime();
    const clampedTimeValues = timeValues.filter((item) => {
      if (!item.read) {
        return false;
      }
      const timestamp = new Date(item.read).getTime();
      if (Number.isNaN(timestamp)) {
        return false;
      }
      return timestamp >= rangeStart && timestamp <= rangeEnd;
    });

    let fillValue: number | null = null;
    if (fillMode === 'zero') {
      fillValue = 0.0;
    } else if (fillMode === 'null') {
      fillValue = null;
    }
    const missingData = this.generateMissingData(clampedTimeValues, interval, date, fillValue, timeBucket);

    const seriesByTimestamp = new Map<number, DateTimeValue>();
    const upsertByReadTimestamp = (item: DateTimeValue) => {
      if (!item.read) {
        return;
      }
      const timestamp = new Date(item.read).getTime();
      if (Number.isNaN(timestamp)) {
        return;
      }
      seriesByTimestamp.set(timestamp, item);
    };

    // Insert real data first so matching points are preserved.
    clampedTimeValues.forEach(upsertByReadTimestamp);

    // Add only missing points to complete the timeline.
    missingData.forEach((item) => {
      if (!item.read) {
        return;
      }
      const timestamp = new Date(item.read).getTime();
      if (Number.isNaN(timestamp) || seriesByTimestamp.has(timestamp)) {
        return;
      }
      seriesByTimestamp.set(timestamp, item);
    });

    return Array.from(seriesByTimestamp.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, value]) => value);
  }

  protected processLabels(allLabels: Date[][]): Date[] {
    if (allLabels.length === 0) {
      return [];
    }
    if (allLabels.length === 1) {
      return allLabels[0];
    }
    // Check if all label arrays have the same length and content
    const firstLabels = allLabels[0];
    const firstLength = firstLabels.length;
    let allMatch = true;
    for (let i = 1; i < allLabels.length; i++) {
      const currentLabels = allLabels[i];
      if (currentLabels.length !== firstLength) {
        allMatch = false;
        break;
      }
      for (let j = 0; j < firstLength; j++) {
        if (firstLabels[j].getTime() !== currentLabels[j].getTime()) {
          allMatch = false;
          break;
        }
      }
      if (!allMatch) break;
    }
    if (!allMatch) {
      console.warn('BaseChartComponent: Label arrays do not match across datasets. Using the longest array.');
      // Find the longest array
      let maxLength = 0;
      let longestLabels: Date[] = [];
      allLabels.forEach((labelArray) => {
        if (labelArray.length > maxLength) {
          maxLength = labelArray.length;
          longestLabels = labelArray;
        }
      });
      return longestLabels;
    }
    return firstLabels;
  }

  /**
   * The day summary provided by the API for the currently displayed data.
   * Every daily chart declares its own `data: AverageEnergyResponse` input, so
   * read it generically from the concrete component instance.
   */
  protected get daySummary(): EnergySummary | undefined {
    return (this as unknown as { data?: AverageEnergyResponse }).data?.summary;
  }

  /**
   * The energy type whose day total this chart should summarize. Each daily
   * chart overrides this so the label only shows its own type. Charts that
   * don't represent a single type leave it undefined and show no label.
   */
  protected get summaryType(): EnergyType | undefined {
    return undefined;
  }

  /**
   * Builds the day total(s) shown in the small label above the chart, scoped to
   * this chart's energy type. Production and consumption have a single value;
   * battery and grid have two.
   */
  protected get daySummaryItems(): Array<{
    label: string;
    color: string;
    parts: Array<{ label?: string; value: string }>;
  }> {
    const s = this.daySummary;
    const type = this.summaryType;
    if (!s || !type) {
      return [];
    }
    const fmt = (v?: number) => `${(v ?? 0).toFixed(1)} kWh`;
    switch (type) {
      case 'production':
        return [
          {
            label: this.getLabel('production'),
            color: this.getColor('production'),
            parts: [{ value: fmt(Math.abs(s.production ?? 0)) }],
          },
        ];
      case 'consumption':
        return [
          {
            label: this.getLabel('consumption'),
            color: this.getColor('consumption'),
            parts: [{ value: fmt(s.consumptionPositive) }],
          },
        ];
      case 'battery':
        return [
          {
            label: this.getLabel('battery'),
            color: this.getColor('battery'),
            parts: [
              { label: 'Laden', value: fmt(s.batteryPositive) },
              { label: 'Entladen', value: fmt(Math.abs(s.batteryNegative ?? 0)) },
            ],
          },
        ];
      case 'grid':
        return [
          {
            label: this.getLabel('grid'),
            color: this.getColor('gridPositive'),
            parts: [
              { label: this.getLabel('gridPositive'), value: fmt(s.gridPositive) },
              { label: this.getLabel('gridNegative'), value: fmt(Math.abs(s.gridNegative ?? 0)) },
            ],
          },
        ];
      default:
        return [];
    }
  }

  protected getColor(option: string): string {
    const root = document.documentElement;
    const colors = {
      production: getComputedStyle(root).getPropertyValue('--color-production-yellow').trim(), // production-yellow
      consumption: getComputedStyle(root).getPropertyValue('--color-consumption-blue').trim(), //  Blue
      battery: getComputedStyle(root).getPropertyValue('--color-battery-green').trim(), // Green
      // Grid colors:
      // - gridPositive: solid black
      // - gridNegative: light grey (SCRUM-199 "einkauf" grey)
      grid: '#a0a0a0',
      gridPositive: '#000000',
      gridNegative: '#a0a0a0',
    };
    // @ts-ignore
    return colors[option] || 'rgba(0, 0, 0, 1)'; // Default black color
  }

  protected getLabel(option: string): string {
    const labels = {
      production: 'Produktion',
      consumption: 'Verbrauch',
      battery: 'Batterie',
      batteryPositive: 'Batterie+',
      batteryNegative: 'Batterie-',
      grid: 'Netz',
      gridPositive: 'Einkauf',
      gridNegative: 'Verkauf',
    };
    //@ts-ignore
    return labels[option] || option;
  }

  /**
   * How far planned (forecast) series are mixed towards white, so they read as a
   * much lighter variant of the measured series they belong to.
   * 0 keeps the original color, 1 is plain white.
   */
  protected readonly plannedBorderLighten = 0.55;
  protected readonly plannedFillLighten = 0.75;

  /** Legend/tooltip name of a planned series. */
  protected getPlannedLabel(label: string): string {
    return `${label} (geplant)`;
  }

  /**
   * Mixes a color towards white. Accepts the formats used across the charts:
   * `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb(...)` and `rgba(...)`.
   * Unparseable values are returned unchanged.
   */
  protected lightenColor(color: string, amount: number): string {
    const parsed = this.parseColor(color);
    if (!parsed) {
      return color;
    }

    const ratio = Math.min(Math.max(amount, 0), 1);
    const lighten = (channel: number) => Math.round(channel + (255 - channel) * ratio);
    return `rgba(${lighten(parsed.r)}, ${lighten(parsed.g)}, ${lighten(parsed.b)}, ${parsed.a})`;
  }

  private parseColor(color: string): { r: number; g: number; b: number; a: number } | null {
    const value = color?.trim();
    if (!value) {
      return null;
    }

    const hexMatch = /^#([0-9a-f]{3,8})$/i.exec(value);
    if (hexMatch) {
      const hex = hexMatch[1];
      const expand = (part: string) => parseInt(part.length === 1 ? part + part : part, 16);
      if (hex.length === 3 || hex.length === 4) {
        return {
          r: expand(hex[0]),
          g: expand(hex[1]),
          b: expand(hex[2]),
          a: hex.length === 4 ? expand(hex[3]) / 255 : 1,
        };
      }
      if (hex.length === 6 || hex.length === 8) {
        return {
          r: expand(hex.slice(0, 2)),
          g: expand(hex.slice(2, 4)),
          b: expand(hex.slice(4, 6)),
          a: hex.length === 8 ? expand(hex.slice(6, 8)) / 255 : 1,
        };
      }
      return null;
    }

    const rgbMatch = /^rgba?\(([^)]+)\)$/i.exec(value);
    if (rgbMatch) {
      const parts = rgbMatch[1]
        .split(/[,/\s]+/)
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .map(Number);
      if (parts.length < 3 || parts.slice(0, 3).some((part) => !Number.isFinite(part))) {
        return null;
      }
      const alpha = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
      return { r: parts[0], g: parts[1], b: parts[2], a: alpha };
    }

    return null;
  }

  /**
   * Appends the datasets of a planned (forecast) status to an already built
   * dataset list. The series are drawn exactly like their measured counterparts,
   * only in a much lighter shade of the same color and in their own stack, so a
   * day that holds both measured and planned values shows them side by side
   * instead of summing them up.
   */
  protected appendPlannedDatasets(
    target: { datasets: any[]; allLabels: Date[][] },
    status: AverageEnergyStatus | undefined,
    config: {
      date: Date;
      interval?: Interval;
      fillMode?: 'zero' | 'null';
      /** `timeBucket` of the measured series, so both share one timeline. */
      timeBucket?: string | null;
      /** Base color of the matching measured series; it is lightened here. */
      color: (deviceKey: string, index: number) => string;
      /** Legend name; receives the resolved device name of the planned series. */
      label: (deviceLabel: string, index: number) => string;
      /** Applied to every point, e.g. to force a sign or convert to kWh. */
      transform?: (item: DateTimeValue) => void;
      /** Extra chart.js dataset properties, mirroring the measured dataset. */
      dataset?: (deviceKey: string, index: number) => Record<string, unknown>;
    },
  ): void {
    if (!status?.deviceTimeValues) {
      return;
    }

    const interval = config.interval ?? this.interval;
    const fillMode = config.fillMode ?? 'zero';
    const timeBucket = config.timeBucket ?? status.timeBucket;

    let index = 0;
    Object.entries(status.deviceTimeValues).forEach(([deviceKey, timeValues]) => {
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) {
        return;
      }

      const completedTimeValues = this.completeData(
        timeValues as DateTimeValue[],
        interval,
        config.date,
        fillMode,
        timeBucket,
      );
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({ ...tv }));
      if (config.transform) {
        rawTimeValues.forEach((item) => config.transform!(item));
      }

      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      target.allLabels.push(datasetLabels);

      const baseColor = config.color(deviceKey, index);
      const deviceLabel = status.deviceNames?.[deviceKey] || deviceKey;
      target.datasets.push({
        label: config.label(deviceLabel, index),
        data: rawTimeValues.map((v) => v.value),
        borderColor: this.lightenColor(baseColor, this.plannedBorderLighten),
        backgroundColor: this.lightenColor(baseColor, this.plannedFillLighten),
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: false,
        borderWidth: 1,
        isPlanned: true,
        ...(config.dataset ? config.dataset(deviceKey, index) : {}),
      });

      index++;
    });
  }

  showDebug = false;

  toggleDebug(): void {
    this.showDebug = !this.showDebug;
  }

  getDebugData(): { label: string; values: { date: string; value: number }[] }[] {
    return [];
  }

  protected getCapLineBorderWidth(): number {
    return this.isMobile() ? 2 : 4;
  }

  protected getDatasetColorValue(color: unknown, fallback = 'rgba(0, 0, 0, 1)'): string {
    if (Array.isArray(color)) {
      const first = color[0];
      return typeof first === 'string' ? first : fallback;
    }
    return typeof color === 'string' ? color : fallback;
  }

  protected isCapLineDataset(dataset: any): boolean {
    return Boolean(dataset?.isCapLine);
  }

  protected styleLegendItemAsCap<T extends { fillStyle?: any; strokeStyle?: any; lineWidth?: number; color?: any }>(
    label: T,
    color: string,
    lineWidth: number = this.getCapLineBorderWidth(),
  ): T {
    return {
      ...label,
      fillStyle: 'rgba(0, 0, 0, 0)',
      strokeStyle: color,
      lineWidth: lineWidth,
      color: color,
    };
  }

  protected get baseTooltipOptions(): ChartOptions['plugins'] {
    const useCompactTooltip = this.isMobile();

    return {
      tooltip: {
        enabled: true,
        // Always show every dataset for the hovered point, even on mobile.
        mode: 'index',
        intersect: false,
        position: 'nearest',
        // Keep tooltip readable while reducing overlap with chart content.
        backgroundColor: 'rgba(0, 0, 0, 0.38)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1,
        padding: useCompactTooltip ? 6 : 10,
        bodySpacing: useCompactTooltip ? 3 : 6,
        boxPadding: useCompactTooltip ? 2 : 4,
        titleFont: {
          size: useCompactTooltip ? 10 : 12,
        },
        bodyFont: {
          size: useCompactTooltip ? 10 : 12,
        },
        // Push tooltip slightly away from the hovered point.
        caretPadding: useCompactTooltip ? 8 : 14,
        // Show a plain box without the pointing arrow/caret.
        caretSize: 0,
        // Always show the color swatch next to each legend entry.
        displayColors: true,
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2) + ' ' + this.unit;
            }
            return label;
          },
          labelColor: (context) => {
            const dataset: any = context.dataset;
            const borderColor = this.getDatasetColorValue(dataset?.borderColor);
            const backgroundColor = this.isCapLineDataset(dataset)
              ? 'rgba(0, 0, 0, 0)'
              : this.getDatasetColorValue(dataset?.backgroundColor, borderColor);
            return {
              backgroundColor,
              borderColor,
              borderWidth: this.isCapLineDataset(dataset)
                ? Math.max(2, Number(dataset?.borderWidth ?? 2))
                : 1,
              borderRadius: 0,
            };
          },
        },
      },
    };
  }

  /**
   * Adds a vertical line annotation to the provided chart options and returns a new options object.
   */
  protected addVerticalLine(options: ChartOptions<'line'>, time: Date, label: string, color: string = 'rgb(149,149,149)'): ChartOptions<'line'> {
    const lineId = `verticalLine_${time}`;
    // Create a shallow clone to ensure Angular change detection picks up the update
    const updatedOptions: ChartOptions<'line'> = { ...options };

    // Ensure nested plugin objects exist
    // @ts-ignore - annotation plugin types may not be present in chart.js types
    if (!updatedOptions.plugins) updatedOptions.plugins = {};
    // @ts-ignore
    if (!updatedOptions.plugins.annotation) updatedOptions.plugins.annotation = { annotations: {} };
    // @ts-ignore
    if (!updatedOptions.plugins.annotation.annotations) updatedOptions.plugins.annotation.annotations = {};

    // @ts-ignore
    updatedOptions.plugins.annotation.annotations[lineId] = {
      type: 'line',
      xScaleID: 'x',
      xMin: time,
      xMax: time,
      borderColor: color,
      borderWidth: 2,
      label: {
        content: label,
        display: true,
        position: 'end',
        backgroundColor: color,
        color: 'white',
        padding: 4,
      },
    };

    return updatedOptions;
  }

  /**
   * Inline plugin that marks the hovered timestamp on the x-axis with a thin
   * vertical line (blue, to stay distinguishable from the grey current-time line).
   * Drawn after the datasets so it sits on top of areas but under the tooltip.
   */
  private hoverTimestampLinePlugin: Plugin = {
    id: 'hoverTimestampLine',
    afterDatasetsDraw: (chart: Chart) => {
      const activeElements = chart.tooltip?.getActiveElements() ?? [];
      if (activeElements.length === 0) {
        return;
      }

      const x = activeElements[0].element.x;
      const { top, bottom, left, right } = chart.chartArea;
      if (x < left || x > right) {
        return;
      }

      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(25, 118, 210, 0.45)';
      ctx.stroke();
      ctx.restore();
    },
  };

  protected chartPlugins: Plugin[] = [this.hoverTimestampLinePlugin];

  /**
   * Adds a thin, barely visible vertical line at the current time.
   * Only drawn when the displayed day is today; otherwise any previously
   * added current-time line is removed so past/future days stay clean.
   */
  protected addCurrentTimeLine(options: ChartOptions<'line'>, displayedDate: Date): ChartOptions<'line'> {
    const now = new Date();

    // Create a shallow clone to ensure Angular change detection picks up the update
    const updatedOptions: ChartOptions<'line'> = { ...options };

    // Ensure nested plugin objects exist
    // @ts-ignore - annotation plugin types may not be present in chart.js types
    if (!updatedOptions.plugins) updatedOptions.plugins = {};
    // @ts-ignore
    if (!updatedOptions.plugins.annotation) updatedOptions.plugins.annotation = { annotations: {} };
    // @ts-ignore
    if (!updatedOptions.plugins.annotation.annotations) updatedOptions.plugins.annotation.annotations = {};

    if (isSameDay(displayedDate, now)) {
      // @ts-ignore
      updatedOptions.plugins.annotation.annotations['currentTimeLine'] = {
        type: 'line',
        xScaleID: 'x',
        xMin: now,
        xMax: now,
        borderColor: 'rgba(128, 128, 128, 0.3)',
        borderWidth: 1,
        label: {
          display: false,
        },
      };
    } else {
      // @ts-ignore
      delete updatedOptions.plugins.annotation.annotations['currentTimeLine'];
    }

    return updatedOptions;
  }

  protected createSunDataLoader() {
    // Create a memoized loader function that doesn't recreate on every access
    return (loaderParams: any) => {
      const requestedIso: string | undefined = loaderParams?.params?.body?.date;
      const requestedDate: Date = requestedIso ? new Date(requestedIso) : new Date();
      const unavailableResponse = { sunrise: undefined, sunset: undefined };
      const handleWeatherError = (error: unknown) => {
        console.log('Weather API does not respond.', error);
        return of(unavailableResponse);
      };
      return from(this.api.invoke(getSunriseSunset, {
        location_id: this.weatherLocationStateService.currentLocation()?.id ?? '',
        at: DateTime.fromISO(requestedDate.toISOString()).setZone('UTC').toISO()!,
      })).pipe(
        catchError((error) => handleWeatherError(error)),
      );
    };
  }

  protected sunDataLoader = this.createSunDataLoader();
}
